import { Accordion, AccordionDetails, AccordionSummary, Paper, Typography } from '@mui/material'
import makeStyles from '@mui/styles/makeStyles'
import classNames from 'classnames'
import { useEffect, useState } from 'react'
import { ActiveOrdersTiles } from '../components/common/ActiveOrdersTiles'
import { FileImportDialog } from '../components/dialogs/FileImportDialog'
import { MassOrderInputDialog } from '../components/dialogs/MassOrderInputDialog'
import { OrderDisplay } from '../components/OrderList/OrderDisplay'
import { OrderTableTopBar } from '../components/OrderList/OrderTableTopBar'
import { useInstrumentApi } from '../hooks/useInstrumentApi'
import { useTranslation } from 'react-i18next'
import { initializeSseOrder } from '../hooks/useOrderSseApi'
import { dayStart, dayEnd } from '../utils/dateRange'
import { useSelector } from '../redux/hooks/useSelector'
import { OrdersByStateCount } from '../redux/reducers/orders'
import { useDispatch } from 'react-redux'
import { ExpandMoreRounded as ExpandMore, AddRounded } from '@mui/icons-material'
import useMobileWidth from '../hooks/useMobileWidth'
import { tokens } from '../styles/tokens'
import { NewLayout } from '../components/newLayout/NewLayout'
import { AlertMessageConfirmation } from '../components/dialogs/AlertMessageConfirmation'
import { useOrderFilters } from '../hooks/useOrderFilters'
import { newTraderDiary } from '../constants/initialStates'
import { openDialog } from '../redux/actions/dialogActions'
import { MASS_ORDER_INPUT_DIALOG_ID } from '../constants/dialogConstants'
import { ColoredIconButton } from '../components/common/ColoredIconButton'

const useStyles = makeStyles(theme => ({
  orderDisplayTable: {
    display: 'flex',
    flexDirection: 'column',
    marginTop: theme.spacing(2),
    flexGrow: 1,
  },
  clientEssentials: {
    width: '25%',
    display: 'flex',
    flexDirection: 'row',
    flexGrow: 1,
  },
  manIcon: {
    marginTop: theme.spacing(1),
  },
  clientPersonalInfoColumn: {
    display: 'flex',
    flexDirection: 'column',
    margin: theme.spacing(1),
  },
  test: {
    background: 'red',
    width: '200px',
    position: 'absolute',
    top: '20px',
    left: '-50px',
    opacity: '0.95',
    textAlign: 'center',
    lineHeight: '25px',
    fontSize: '16px',
    letterSpacing: '1px',
    color: '#f0f0f0',
    transform: 'rotate(-45deg)',
    paddingRight: '18px',
  },

  searchBar: {
    background: tokens.color.white,
    border: `1px solid ${tokens.color.neutral[200]}`,
    boxShadow: 'none',
    padding: 0,
    borderRadius: tokens.radius.lg,
    margin: '16px 0',
    borderTop: 0,
    '&.Mui-expanded': {
      margin: '16px 0',
    },
    '&::before': {
      display: 'none',
    },
    // Na desktopu je akordeon jen neviditelny obal - viditelna karta je vnitrni
    // toolbar (OrderTableTopBar), stejny vzor jako Klienti/Instrumenty.
    // Svisle mezery ridi segment track, ne akordeon (important prebiji emotion styl MUI).
    [theme.breakpoints.up('md')]: {
      background: 'transparent',
      border: 'none',
      margin: '0 !important',
    },
  },
  searchBarHeadText: {
    margin: 0,
    padding: 0,
  },
  searchBarHead: {
    padding: 0,
    margin: 0,
    dispay: 'flex',
    [theme.breakpoints.up('md')]: {
      display: 'none',
    },
  },
  accDetail: {
    padding: theme.spacing(2),
    [theme.breakpoints.up('md')]: {
      padding: 0,
    },
  },
  // Panel s tlacitkem Novy pokyn - na mobilu viditelny, i kdyz jsou filtry sbalene.
  topActionsRow: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: theme.spacing(1),
  },
  accordionWrapper: {
    flexGrow: 1,
    minWidth: 0,
  },
  topActionButton: {
    marginTop: theme.spacing(2),
  },
}))

const ribon = process.env.REACT_APP_ENV_RIBBON

export const OrderList = () => {
  const classes = useStyles()
  const { getOrderTableRows, updateOrderDiary, exportOrderDiary } = useInstrumentApi()
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const isMobile = useMobileWidth()
  const [filtersExpanded, setFiltersExpanded] = useState(isMobile)
  const [showConfirmation, setShowConfirmation] = useState<boolean>(false);
  const [orderToExport, setOrderToExport] = useState<number[]>([]);

  const [dateFrom, setDateFrom] = useState<Date>(new Date())
  const [dateTo, setDateTo] = useState<Date>(new Date())

  useEffect(() => {
    initializeSseOrder(dispatch, null, dayStart(dateFrom), dayEnd(dateTo))
  }, [dateFrom, dateTo])

  const [ordersByStateCount, setOrdersByStateCount] = useState([] as OrdersByStateCount[])
  const orderSse = useSelector(state => state.orders.orderSse)
  const orderTableRows = useOrderFilters();

  useEffect(() => {
    if (orderSse && orderSse.userStates && orderSse.userStates.length > 0) {
      setOrdersByStateCount([...orderSse.userStates[0].states])
    }
  }, [orderSse])

  useEffect(() => {
    isMobile ? setFiltersExpanded(false) : setFiltersExpanded(true)
  }, [isMobile])

  useEffect(() => {
    getOrderTableRows(null, null, null, null)
  }, [])

  const handleChange = () => {
    setFiltersExpanded(!filtersExpanded)
  }

  const setOrderCheckInner = (orderId: number, valueDiary: boolean) => {
    updateOrderDiary(orderId, !valueDiary);
  }

  const setShowConfirmationDialog = (open: boolean) => {
    let tmp = [];
    for ( let i=0; i<orderTableRows.length; i++) {
      tmp.push(orderTableRows[i].orderId);
    }
    setOrderToExport(tmp)
    setShowConfirmation(open);
  }

  const handleConfirmation = () => {
    setShowConfirmation(false);
    exportOrderDiary(orderToExport);
    setOrderToExport([]);
  }

  const handleCloseConfirmation = () => {
    setShowConfirmation(false);
    setOrderToExport([]);
  }

  return (
    <NewLayout pageName={t('ORDER_OVERVIEW')}>
      <ActiveOrdersTiles ordersByStateCount={ordersByStateCount} />
      <div className={classes.topActionsRow}>
        <Accordion elevation={0} className={classNames(classes.searchBar, classes.accordionWrapper)} expanded={filtersExpanded} onChange={handleChange}>
          <AccordionSummary expandIcon={<ExpandMore />} className={classes.searchBarHead}>
            <p className={classes.searchBarHeadText}>Filtry</p>
          </AccordionSummary>
          <AccordionDetails className={classes.accDetail}>
            <OrderTableTopBar
                dateFrom={dateFrom}
                setDateFrom={setDateFrom}
                dateTo={dateTo}
                setDateTo={setDateTo}
                setShowConfirmation={setShowConfirmationDialog}
            />
          </AccordionDetails>
        </Accordion>
        {isMobile && (
          <ColoredIconButton
            className={classes.topActionButton}
            onClick={() => dispatch(openDialog(MASS_ORDER_INPUT_DIALOG_ID))}
            size="small"
            icon={<AddRounded />}
          />
        )}
      </div>
      <OrderDisplay
        setOrderCheck={setOrderCheckInner}
      />
      <MassOrderInputDialog traderDiary={newTraderDiary} isFromTrader={false}/>
      <FileImportDialog />
      <AlertMessageConfirmation
        alertOpen={showConfirmation}
        onOk={handleConfirmation}
        onCancle={handleCloseConfirmation}
        alertText={t('INVESTMENT_DIARY_MESSAGE')}
      />
      
    </NewLayout>
  )
}
